import { useCurrentSite } from '@hotjar/site';
import { useNotify } from '@hotjar/webapp/hooks/useNotify';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeleteSurveyResponseMutation } from '../domain/Surveys/query';
import type { SurveyResponseDto } from '../domain/Surveys/Responses/SurveyResponseDto';

interface DeleteResponseProps {
  deleteResponse: (response: SurveyResponseDto) => Promise<void>;
  isDeleteResponsePending: boolean;
  isDeleteResponseConfirmationRequired: boolean;
  onDeleteResponseCancel: () => void;
  onDeleteResponseConfirm: () => void;
}

export const useDeleteSurveyResponse = (surveyId: number | undefined): DeleteResponseProps => {
  const { data: site } = useCurrentSite();
  const deleteSurveyResponseMutation = useDeleteSurveyResponseMutation();

  const { success, error } = useNotify();
  const { t } = useTranslation('feedback');

  const [responseId, setResponseId] = useState<undefined | number>(undefined);
  const [isDeleteResponsePending, setIsDeleteResponsePending] = useState(false);

  const deleteResponse = useCallback(async (response: SurveyResponseDto) => {
    setResponseId(response.id);
  }, []);

  const onDeleteResponseConfirm = useCallback(async () => {
    if (site === undefined || responseId === undefined || surveyId === undefined) return;

    try {
      setIsDeleteResponsePending(true);
      await deleteSurveyResponseMutation.mutateAsync({
        siteId: site?.id,
        surveyId,
        responseId,
      });
      success(t('survey-response.notification.delete-success'));
    } catch {
      error(t('survey-response.notification.delete-error'));
    } finally {
      setResponseId(undefined);
      setIsDeleteResponsePending(false);
    }
  }, [site, success, error, deleteSurveyResponseMutation, t, surveyId, responseId]);

  const onDeleteResponseCancel = useCallback(() => {
    setResponseId(undefined);
    setIsDeleteResponsePending(false);
  }, []);

  return {
    deleteResponse,
    isDeleteResponsePending,
    isDeleteResponseConfirmationRequired: responseId !== undefined,
    onDeleteResponseCancel,
    onDeleteResponseConfirm,
  };
};
